export const MINUTES_PER_DAY = 480;

export const HOURS_MIN = 0.25;
export const HOURS_MAX = 24;
export const HOURS_STEP = 0.25;

export const DAYS_MIN = 1;
export const DAYS_MAX = 99;
export const DAYS_STEP = 0.5;

const MINUTES_PER_HOUR = 60;

export function toMinutes(value, unit) {
  const numericValue = Number(value);
  return numericValue * (unit === "days" ? MINUTES_PER_DAY : MINUTES_PER_HOUR);
}

export function buildLabel(value, unit, exceeded = false) {
  if (exceeded) return "99+ days";

  const numericValue = Number(value);
  if (unit === "days") {
    return `${numericValue} ${numericValue === 1 ? "day" : "days"}`;
  }

  const minutes = toMinutes(numericValue, "hours");
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  const remainingMinutes = minutes % MINUTES_PER_HOUR;

  if (hours && remainingMinutes) return `${hours}h ${remainingMinutes}m`;
  if (hours) return `${hours}h`;
  return `${remainingMinutes}m`;
}

export function formatMinutes(minutes) {
  if (minutes == null || minutes === "") return "—";

  const numericMinutes = Number(minutes);
  if (!Number.isFinite(numericMinutes) || numericMinutes <= 0) return "—";

  const hours = Math.floor(numericMinutes / MINUTES_PER_HOUR);
  const remainingMinutes = numericMinutes % MINUTES_PER_HOUR;

  if (hours && remainingMinutes) return `${hours}h ${remainingMinutes}m`;
  if (hours) return `${hours}h`;
  return `${remainingMinutes}m`;
}

function isOnStep(value, min, step) {
  const steps = (value - min) / step;
  return Math.abs(steps - Math.round(steps)) < Number.EPSILON * 10;
}

export function validateTimeTaken({ value, unit, exceeded, note } = {}) {
  if (unit !== "hours" && unit !== "days") {
    return { ok: false, error: "Select hours or days." };
  }

  const numericValue = Number(value);
  const min = unit === "hours" ? HOURS_MIN : DAYS_MIN;
  const max = unit === "hours" ? HOURS_MAX : DAYS_MAX;
  const step = unit === "hours" ? HOURS_STEP : DAYS_STEP;

  if (
    !Number.isFinite(numericValue) ||
    numericValue < min ||
    numericValue > max ||
    !isOnStep(numericValue, min, step)
  ) {
    return {
      ok: false,
      error: `Enter ${unit} from ${min} to ${max} in increments of ${step}.`,
    };
  }

  if (exceeded && (unit !== "days" || numericValue !== DAYS_MAX)) {
    return { ok: false, error: "The 99+ option must use the 99-day floor." };
  }

  if (exceeded && !String(note || "").trim()) {
    return { ok: false, error: "Enter the actual timeframe in the required note." };
  }

  return { ok: true, error: "" };
}

export function buildTimeTakenCore({ value, unit, exceeded, note }) {
  const numericValue = Number(value);
  const isExceeded = Boolean(exceeded);

  return {
    timeTakenValue: numericValue,
    timeTakenUnit: unit,
    timeTakenLabel: buildLabel(numericValue, unit, isExceeded),
    timeTakenExceededLimit: isExceeded,
    timeTakenNote: isExceeded ? String(note || "").trim() || null : null,
    minutes: toMinutes(numericValue, unit),
  };
}
